<template>
  <div class="col-md-8 offset-md-2">
    <div class="row" v-if="catchup">
      <div class="col-md-6">
        <card>
          <h5 slot="header">
            Catch-up with {{ catchup.contact.firstName }}
            {{ catchup.contact.lastName }}
          </h5>

          <div>
            <div class="form-group row">
              <label class="col-sm-4 col-form-label">Date</label>

              <fg-input class="col-sm-8">
                <el-date-picker
                  type="date"
                  v-validate="'required'"
                  data-vv-as="date"
                  name="date"
                  placeholder="Catch-up date"
                  format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd"
                  v-model="catchup.date"
                ></el-date-picker>
              </fg-input>
            </div>

            <div class="form-group row">
              <label class="col-sm-4 col-form-label">Catch-up type</label>

              <div class="col-sm-8">
                <select v-model="catchup.type" class="form-control">
                  <option value="in_person">In person meetup</option>
                  <option value="phone_call">Phone call</option>
                  <option value="video_call"
                    >Video call (Skype, Zoom etc.)</option
                  >
                  <option value="messaging"
                    >Messaging (WhatsApp, Messenger etc.)</option
                  >
                  <option value="email">Email</option>
                  <option value="other">Other</option>
                  <option :value="null">Not specified</option>
                </select>
              </div>
            </div>

            <div class="form-group row" v-if="catchup.type === 'in_person'">
              <label class="col-sm-4 col-form-label">Location</label>
              <fg-input
                class="form-control-md col-sm-8"
                v-model="catchup.location"
                type="text"
                name="location"
                placeholder="e.g. Blue Moon Pub"
                addon-left-icon="now-ui-icons location_pin"
              ></fg-input>
            </div>

            <div class="form-group row">
              <label class="col-sm-4 col-form-label">Notes</label>
              <div class="col-sm-8">
                <textarea
                  v-if="!catchup.date || catchupInFuture"
                  class="form-control"
                  v-model="catchup.beforeNote"
                  placeholder="Remember to ask about..."
                  rows="5"
                ></textarea>
                <textarea
                  v-else-if="catchup.date && !catchupInFuture"
                  class="form-control"
                  v-model="catchup.afterNote"
                  placeholder="Need to follow up our discussion about..."
                  rows="5"
                ></textarea>
                <small class="form-text text-muted">
                  Markdown supported (
                  <a
                    href="https://www.markdownguide.org/basic-syntax/"
                    target="_blank"
                    >syntax guide</a
                  >)
                </small>
              </div>
            </div>

            <div class="alert alert-warning" v-if="updateCatchupError">
              {{ updateCatchupError }}
            </div>
          </div>
          <div slot="footer">
            <div class="row">
              <div class="col-6">
                <button
                  class="btn btn-default"
                  title="Delete catch-up"
                  v-on:click="openDeleteCatchupAlert"
                >
                  <i class="fa fa fa-trash-o mr-2"></i> Delete
                </button>
              </div>
              <div class="col-6 text-right">
                <n-button
                  type="success"
                  @click.native="updateCatchup"
                  :disabled="updatingCatchup"
                >
                  <span v-if="!updatingCatchup">
                    <i class="fa fa-check"></i>&nbsp; Save
                  </span>
                  <span v-else>
                    <i class="fa fa-circle-o-notch fa-spin"></i>&nbsp; Please
                    wait&hellip;
                  </span>
                </n-button>
              </div>
            </div>
          </div>
        </card>
      </div>

      <div class="col-md-6">
        <!-- Contact card here -->
        <contact-card
          :contact="catchup.contact"
          :link="`/contacts/${catchup.contact.id}`"
        ></contact-card>
      </div>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert2";
import AddToCalendar from "vue-add-to-calendar";
import moment from "moment";
import _ from "underscore";

import ContactCard from "src/components/Cards/ContactCard.vue";
import { DatePicker } from "element-ui";

import MailtoUI from "mailtoui/dist/mailtoui-min.js";
import { request } from "http";

export default {
  props: {
    catchupId: {
      type: String,
      required: true
    }
  },
  components: {
    ContactCard,
    [DatePicker.name]: DatePicker,
    AddToCalendar
  },
  data() {
    return {
      catchup: null,
      loadingCatchup: false,
      updatingCatchup: false,
      updateCatchupError: null
    };
  },
  computed: {
    catchupInFuture: function() {
      if (this.catchup.date) {
        if (moment(this.catchup.date, "YYYY-MM-DD").isAfter()) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    }
  },
  methods: {
    loadCatchup: function() {
      this.loadingCatchup = true;
      this.axios
        .get(`/v1/catchups/${this.catchupId}?expand=contact`)
        .then(response => {
          this.loadingCatchup = false;
          this.catchup = response.data;

          // run MailtoUI
          this.$nextTick(() => {
            MailtoUI.run();
          });
        })
        .catch(error => {
          this.loadingCatchup = false;
          // TODO: handle error in UI
        });
    },
    updateCatchup: function() {
      const instance = this;
      instance.updatingCatchup = true;

      const requestBody = _.omit(instance.catchup, [
        "id",
        "contactId",
        "contact",
        "createdAt",
        "updatedAt",
        "ownerId"
      ]);

      instance.axios
        .patch(`/v1/catchups/${instance.catchup.id}`, requestBody)
        .then(response => {
          // delay to wait for API's afterCreate (contact) hook
          setTimeout(() => {
            instance.updatingCatchup = false;
            instance.loadCatchup();

            this.$notify({
              message: `Catchup with <b>${instance.catchup.contact.firstName} ${instance.catchup.contact.lastName}</b> updated`,
              timeout: 5000,
              icon: "fa fa-check",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "success"
            });
          }, 1000);
        })
        .catch(err => {
          instance.updatingCatchup = false;
          // TODO: handle the error
        });
    },
    openDeleteCatchupAlert: function() {
      const instance = this;
      swal({
        title: "Are you sure?",
        text: `This catch-up will be deleted`,
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "btn btn-danger btn-fill",
        cancelButtonClass: "btn btn-default btn-fill",
        confirmButtonText: `Yes, delete it`,
        buttonsStyling: false,
        reverseButtons: true
      }).then(function(result) {
        if (result.value) {
          instance.axios
            .delete(`/v1/catchups/${instance.catchup.id}`)
            .then(response => {
              instance.$emit("catchup-deleted", instance.catchup);

              instance.$router.push(`/contacts/${instance.catchup.contactId}`);

              swal({
                title: "Deleted!",
                text: "Catch-up deleted.",
                type: "success",
                confirmButtonClass: "btn btn-success btn-fill",
                buttonsStyling: false
              });
            })
            .catch(err => {
              // TODO: handle the error
            });
        }
      });
    }
  },
  mounted() {
    this.loadCatchup();
  }
};
</script>

<style>
.mailtoui-brand {
  display: none;
}
</style>
